import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';

export default function DoorsUI() {
  const { user } = useContext(AuthContext);
  const [doorType, setDoorType] = useState('');
  const [thickness, setThickness] = useState('');
  const [size, setSize] = useState('');
  const [designName, setDesignName] = useState('');
  const [stock, setStock] = useState(0);
  const [damagedStock, setDamagedStock] = useState(0);
  const [image, setImage] = useState(null);
  const [doorTypes, setDoorTypes] = useState([]);
  const [thicknessOptions, setThicknessOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchParameters();
  }, []);

  const fetchParameters = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/doors/parameters/all`);
      const params = response.data;
      setDoorTypes(params.filter(param => param.parameterType === 'Door Type'));
      setThicknessOptions(params.filter(param => param.parameterType === 'Thickness'));
      setSizeOptions(params.filter(param => param.parameterType === 'Size'));
    } catch (error) {
      console.error('Error fetching door parameters:', error);
      Swal.fire('Error', 'Failed to load parameters', 'error');
    }
  };

  const isThicknessRequired = () => {
    const selectedType = doorTypes.find(type => type._id === doorType);
    return selectedType?.isThickness;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!doorType || (isThicknessRequired() && !thickness) || !size || !designName || !image) {
      Swal.fire('Error', 'Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('doorType', doorType);
    formData.append('thickness', thickness);
    formData.append('size', size);
    formData.append('designName', designName);
    formData.append('stock', stock);
    formData.append('damaged_stock', damagedStock);
    formData.append('image', image);
    formData.append('createdBy', user.user._id);

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/doors`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      Swal.fire('Success', 'Door added successfully', 'success');
      resetForm();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.error || error.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  const resetForm = () => {
    setDoorType('');
    setThickness('');
    setSize('');
    setDesignName('');
    setStock(0);
    setDamagedStock(0);
    setImage(null);
  };

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '42px',
      borderColor: '#d1d5db',
      '&:hover': {
        borderColor: '#93c5fd'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#dbeafe' : 'white',
      color: state.isSelected ? 'white' : '#1f2937'
    })
  };
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-xl rounded-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Add New Door</h1>
            <p className="text-gray-600 mt-2">Fill in the details to add a new door to your inventory</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Form Fields */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Door Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={doorTypes.map(type => ({
                      value: type._id,
                      label: type.parameterName
                    }))}
                    value={doorTypes.find(type => type._id === doorType) ? 
                      { value: doorType, label: doorTypes.find(type => type._id === doorType)?.parameterName } : null}
                    onChange={(option) => setDoorType(option ? option.value : '')}
                    placeholder="Select Door Type"
                    isClearable
                    styles={customSelectStyles}
                    className="text-sm"
                  />
                </div>

                {isThicknessRequired() && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Thickness <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={thicknessOptions.map(thick => ({
                        value: thick._id,
                        label: thick.parameterName
                      }))}
                      value={thicknessOptions.find(thick => thick._id === thickness) ? 
                        { value: thickness, label: thicknessOptions.find(thick => thick._id === thickness)?.parameterName } : null}
                      onChange={(option) => setThickness(option ? option.value : '')}
                      placeholder="Select Thickness"
                      isClearable
                      styles={customSelectStyles}
                      className="text-sm"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Size <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={sizeOptions.map(s => ({
                      value: s._id,
                      label: s.parameterName
                    }))}
                    value={sizeOptions.find(s => s._id === size) ? 
                      { value: size, label: sizeOptions.find(s => s._id === size)?.parameterName } : null}
                    onChange={(option) => setSize(option ? option.value : '')}
                    placeholder="Select Size"
                    isClearable
                    styles={customSelectStyles}
                    className="text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Design Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={designName}
                    onChange={e => setDesignName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                    placeholder="Enter design name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Opening Stock
                    </label>
                    <input
                      type="number"
                      value={stock}
                      onChange={e => setStock(Number(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                      placeholder="Enter stock quantity"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Damaged Stock
                    </label>
                    <input
                      type="number"
                      value={damagedStock}
                      onChange={e => setDamagedStock(Number(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                      placeholder="Enter damaged stock"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column - Image Section */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Door Image <span className="text-red-500">*</span>
                  </label>
                  
                  {!image ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition duration-200 h-80">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => setImage(e.target.files[0])}
                        className="hidden"
                        id="image-upload"
                      />
                      <label htmlFor="image-upload" className="cursor-pointer h-full flex flex-col justify-center">
                        <div className="text-gray-600">
                          <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <p className="text-lg font-medium text-gray-900 mb-2">Upload Door Image</p>
                          <p className="text-sm text-gray-500 mb-1">Click here or drag and drop</p>
                          <p className="text-xs text-gray-400">PNG, JPG, JPEG up to 10MB</p>
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="border border-gray-300 rounded-lg overflow-hidden">
                        <img 
                          src={URL.createObjectURL(image)} 
                          alt="Door Preview" 
                          className="w-full h-80 object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setImage(null)}
                        className="absolute top-3 right-3 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm transition duration-200 shadow-lg"
                        title="Remove image"
                      >
                        ×
                      </button>
                      <div className="absolute bottom-3 left-3 bg-black bg-opacity-75 text-white px-3 py-1 rounded-lg text-sm">
                        {image.name}
                      </div>
                    </div>
                  )}
                </div>

                {image && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Image Details</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><span className="font-medium">Name:</span> {image.name}</p>
                      <p><span className="font-medium">Size:</span> {(image.size / 1024 / 1024).toFixed(2)} MB</p>
                      <p><span className="font-medium">Type:</span> {image.type}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => document.getElementById('image-upload').click()}
                      className="mt-3 w-full bg-blue-50 hover:bg-blue-100 text-blue-600 py-2 px-4 rounded-lg transition duration-200 text-sm font-medium"
                    >
                      Change Image
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={e => setImage(e.target.files[0])}
                      className="hidden"
                      id="image-upload"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    'Add Door'
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={loading}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition duration-200"
                >
                  Reset
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
